import { useRef, useImperativeHandle } from 'react';
import { Button, ControlLabel, Dialog, DialogContent, DialogRef, Stack, Table, TableBody, TableCell, TableHead, TableRow } from 'src/app/components'
import LineWithText from 'src/app/components/Display/LineWithText/LineWithText'
import { HookFormProvider, InputSelect, InputTextField } from 'src/app/components/HookForm'
import InputSelectMall from 'src/app/components/HookForm/InputSelect/InputSelect.mall'
import { SelectGiftFormModel, useSelectGiftPopup } from './SelectGiftPopup.hook';
import InputNumberMinPlus from 'src/app/components/Inputs/InputNumberMinPlus/InputNumberMinPlus';
import { SalesPromoSelectGiftPrizesModel } from 'src/app/modules/campaign/models/SalesPromo.model';
import { safeArray } from 'src/app/utils/array-utils';
import { LoadingIcon } from 'src/app/components/Icons';
import { twMerge } from 'tailwind-merge';
import { Theme } from 'src/app/components/HookForm/HookForm/HookForm.theme';

interface SelectGiftPopupProps {
  dialogRef: React.RefObject<DialogRef>;
  save?: () => void;
  cancel?: () => void;
  id?: string; // <-- example of an additional prop you expect
  [key: string]: any; // <-- allows any other props (optional)
  params: { campaign_id?: string, member_id?: string, mission_stamp_id?: string }
}

const SelectGiftPopup = ({ dialogRef, save, cancel, params, ...props }: SelectGiftPopupProps) => {
  const localRef = useRef<DialogRef>(null);
  const state = useSelectGiftPopup(params, clickCancel)

  useImperativeHandle(dialogRef, () => ({
    show: () => localRef.current?.show(
      state.detail.refetch()
    ),
    hide: () => localRef.current?.hide(),
  }), []);

  const submit = state.selectGiftForm.handleSubmit(async ({ ...formData }) => {
    const wrapUpDirectGift = formData.direct_prizes?.reduce<SalesPromoSelectGiftPrizesModel[]>(
      (prev, item) => {
        const existing = prev.find(obj => obj.id === item.id);
        if (existing && existing.quantity) {
          existing.quantity += item.quantity || 0;
        } else {
          prev.push({ ...item });
        }
        return prev;
      },
      [] // <-- this is the required initial value
    );
    const prizes = [...safeArray(wrapUpDirectGift), ...formData.prizes, ...(formData.extra_point_prize ? [formData.extra_point_prize] : [])].filter(el => el.quantity)
    const payload: SelectGiftFormModel = {
      claim_directory_id: formData.directory?.id,
      prizes
    }
    hitPostAPI(payload);
  })

  const hitPostAPI = async (payload: SelectGiftFormModel) => {
    try {
      await state.submit.mutateAsync(payload)
      clickCancel()
    } catch (error: any) {
      console.error(error)
    }
  }

  function clickCancel() {
    state.reset();
    props.params = {}
    cancel && cancel();
    localRef.current?.hide();
  }

  const renderItemSummaryTable = (item: { name?: string, quantity?: number }, index: number) => {
    return (
      <TableRow key={`${item.name}-${index}`} className='last:border-0'>
        <TableCell value={item?.name} />
        <TableCell value={item?.quantity} />
      </TableRow>
    )
  }

  const renderItemDirectGiftTable = (item: SalesPromoSelectGiftPrizesModel, index: number) => {
    return (<TableRow key={`${index}directgift`} className='!border-0'>
      <TableCell className='w-[1em]'>&nbsp;</TableCell>
      <TableCell>
        <InputSelect
          placeholder="Select prize"
          className="w-full"
          isMulti={false}
          rule={{ required: true }}
          form={state.selectGiftForm}
          name={`direct_prizes.${index}`}
          options={state.directGift.directGiftOptions}
          getOptionLabel={(option) => option.name!}
          getOptionValue={(option) => option.id!}
          disabled={!state.valClaimLocation}
          onChangeValue={(selected: any) => state.directGift.changeValueDirectPrize(selected, index)} // store the object
        />
      </TableCell>
      <TableCell value={state.valDirectGift?.[index]?.remaining_quantity} colSpan={2} />
    </TableRow>)
  };

  const renderItemPrizeTable = (item: SalesPromoSelectGiftPrizesModel, index: number) => {
    return (
      <TableRow key={`${index}gift`} className='last:border-0'>
        <TableCell index={index + (state.hasDirectGift ? 1 : 0)} className='w-[1em]' />
        <TableCell value={item?.name} />
        <TableCell value={item?.remaining_quantity} />
        <TableCell contentWrapperClassName="justify-end">
          <InputNumberMinPlus min={0} max={item?.remaining_quantity} disabled={!state.valClaimLocation} disable={{ plus: state.disableAllIncrement }} name={`prizes.${index}.quantity`} />
        </TableCell>
      </TableRow>
    )
  }

  const renderDirectGift = () => {
    if (!state.hasDirectGift) return null;
    return (
      <TableRow key="direct-gift-header">
        <TableCell index={0} className='w-[1em]' />
        <TableCell colSpan={2}>Direct Gift</TableCell>
        <TableCell contentWrapperClassName="justify-end">
          <InputNumberMinPlus
            min={0}
            disabled={!state.valClaimLocation}
            disable={{ plus: (state.disableIncreaseRandomGift || state.disableAllIncrement) }}
            name="direct_prizes_quantity"
            onChangeVal={state.directGift.directGiftChange}
          />
        </TableCell>
      </TableRow>
    );
  };


  const renderExtraPoint = () => {
    if (!state.detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'EXTRA_POINT')) return null;
    return (
      <TableRow key="extra-point">
        <TableCell index={state.gift.fields.length + 1} className='w-[1em]' />
        <TableCell value={state.valExtraPoint?.name} />
        <TableCell value={state.valExtraPoint?.remaining_quantity} />
        <TableCell contentWrapperClassName="justify-end">
          <InputTextField
            type="number"
            name={`extra_point_prize.quantity`}
            disabled={!state.valClaimLocation || (!state.valExtraPoint?.quantity && state.disableAllIncrement)}
          />
        </TableCell>
      </TableRow>
    );
  };


  const tableRows = [
    renderDirectGift(),
    ...state.directGift.fields.map(renderItemDirectGiftTable),
    ...state.gift.fields.map(renderItemPrizeTable),
    renderExtraPoint(),
  ].filter(Boolean);

  return <Dialog
    size="lg"
    ref={localRef}
    onHide={clickCancel}
    title="Select Gift for Offline Games"
  >
    <DialogContent>
      {(state.detail.isLoading || state.detail.isFetching) && <div className={twMerge(Theme.mask)}>
        <LoadingIcon />
      </div>}
      <HookFormProvider form={state.selectGiftForm}>
        <Stack>
          <ControlLabel label="Claim Location">
            <InputSelectMall placeholder="Type to add" isMulti={false} form={state.selectGiftForm} rule={{ required: true }} />
          </ControlLabel>
          <LineWithText text={`Prize/Round: ${state.totalPrizeCollected}/${state.detail?.data?.prize_quantity_per_round}`} />
        </Stack>
        <Stack className='pb-6 items-end' direction='col'>
          <div className="w-full border-2 px-3 rounded-lg">
            <Table classNames={{ container: 'mb-0 pb-0' }}>
              <TableHead>
                <TableRow>
                  <TableCell index className='w-[1em]' />
                  <TableCell>Gift/Voucher</TableCell>
                  <TableCell>Remaining Quota</TableCell>
                  <TableCell contentWrapperClassName="justify-end">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows}
              </TableBody>
            </Table>
          </div>
          <div className='border-2 px-3 rounded-lg'>
            <Table classNames={{ container: 'mb-0 pb-0' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Prize Type</TableCell>
                  <TableCell>Total Qty Earned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.summaryTable?.map(renderItemSummaryTable)}
              </TableBody>
            </Table>
          </div>
        </Stack>
      </HookFormProvider>
      <Stack
        direction="row"
        justify="between"
      >
        <Button variant='outlined' className='min-w-[128px]' onClick={clickCancel}>Cancel</Button>
        <Button className='min-w-[128px]' disabled={!state.valClaimLocation} loading={state.submit.isLoading} onClick={() => submit()}>Save</Button>
      </Stack>
    </DialogContent>
  </Dialog>
}

export default SelectGiftPopup
